<script setup>
import { ref, computed, onMounted } from 'vue';
import { supabase } from '../db/supabaseClient.js';
import { currentUser } from '../db/auth.js';
import { uploadFileToMinIO, deleteFileFromMinIO } from '../db/minioClient.js';
import ConfirmModal from '../components/ConfirmModal.vue';
import AlertModal from '../components/AlertModal.vue';
import ChatAssistant from '../components/ChatAssistant.vue';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const user = currentUser();
const isAdmin = computed(() => {
  const role = String(user?.role || "").toLowerCase();
  return role === "admin" || role === "superadmin" || user?.role_id === 1;
});

const invoices = ref([]);
const customers = ref([]);
const products = ref([]);
const loading = ref(false);
const showModal = ref(false);
const isEditing = ref(false);

// Modales de alerta y confirmación
const showAlertModal = ref(false);
const alertModalConfig = ref({
  type: 'info',
  title: '',
  message: '',
  autoClose: 0
});

const showConfirmModal = ref(false);
const confirmModalConfig = ref({
  type: 'warning',
  title: '',
  message: '',
  onConfirm: () => { }
});

const form = ref({
  id_invoices: null,
  invoice_number: '',
  customer_id: '',
  invoice_date: new Date().toISOString().split('T')[0],
  due_date: '',
  items: [],
  subtotal: 0,
  tax_rate: 0.16,
  tax_amount: 0,
  total_amount: 0,
  pdf_url: '',
  notes: '',
  status: 'pending'
});

const statuses = [
  { value: 'pending', label: 'Pendiente', color: '#f59e0b' },
  { value: 'paid', label: 'Pagada', color: '#10b981' },
  { value: 'overdue', label: 'Vencida', color: '#ef4444' },
  { value: 'cancelled', label: 'Cancelada', color: '#6b7280' }
];

const searchQuery = ref('');

const filteredInvoices = computed(() => {
  if (!searchQuery.value.trim()) return invoices.value;
  const query = searchQuery.value.toLowerCase();
  return invoices.value.filter(inv =>
    inv.invoice_number.toLowerCase().includes(query) ||
    inv.customer_name?.toLowerCase().includes(query) ||
    inv.status.toLowerCase().includes(query)
  );
});

onMounted(async () => {
  if (!isAdmin.value) {
    alertModalConfig.value = {
      type: 'error',
      title: 'Acceso Denegado',
      message: 'Solo los administradores pueden acceder a este apartado.',
      autoClose: 0
    };
    showAlertModal.value = true;
    return;
  }
  await loadCustomers();
  await loadProducts();
  await loadInvoices();
});

async function loadCustomers() {
  try {
    // Cargar todos los clientes de la tabla customers
    // NOTA: Si no ves todos los clientes, ejecuta el script:
    // database/sync_users_to_customers.sql en Supabase
    const { data, error } = await supabase
      .from('customers')
      .select('*')
      .order('name');

    if (error) throw error;
    customers.value = data || [];
  } catch (error) {
    console.error('Error cargando clientes:', error);
  }
}

async function loadProducts() {
  try {
    const { data, error } = await supabase
      .from('products')
      .select('id_products, name, unit_price, sku, description')
      .eq('active', true)
      .order('name');

    if (error) throw error;
    products.value = data || [];
  } catch (error) {
    console.error('Error cargando productos:', error);
  }
}

async function loadInvoices() {
  loading.value = true;
  try {
    const { data, error } = await supabase
      .from('invoices')
      .select(`
        *,
        customer:customers(name)
      `)
      .order('created_at', { ascending: false });

    if (error) throw error;

    invoices.value = (data || []).map(inv => ({
      ...inv,
      customer_name: sanitizeText(inv.customer?.name) || 'Sin cliente',
      // Sanitizar items si existen
      items: inv.items ? (typeof inv.items === 'string'
        ? sanitizeItems(JSON.parse(inv.items))
        : sanitizeItems(inv.items)) : []
    }));
  } catch (error) {
    console.error('Error cargando facturas:', error);
    alertModalConfig.value = {
      type: 'error',
      title: 'Error',
      message: 'No se pudieron cargar las facturas',
      autoClose: 0
    };
    showAlertModal.value = true;
  } finally {
    loading.value = false;
  }
}

function generateInvoiceNumber() {
  const date = new Date();
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
  return `INV-${year}${month}-${random}`;
}

function addItem() {
  form.value.items.push({
    product_id: null,
    description: '',
    quantity: 1,
    unit_price: 0,
    amount: 0
  });
}

function onProductSelect(item) {
  const product = products.value.find(p => p.id_products === item.product_id);
  if (product) {
    item.description = product.name;
    item.unit_price = parseFloat(product.unit_price) || 0;
    calculateItemAmount(item);
  }
}

function removeItem(index) {
  form.value.items.splice(index, 1);
  calculateTotals();
}

function calculateItemAmount(item) {
  item.amount = (parseFloat(item.quantity) || 0) * (parseFloat(item.unit_price) || 0);
  calculateTotals();
}

function calculateTotals() {
  form.value.subtotal = form.value.items.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
  form.value.tax_amount = form.value.subtotal * parseFloat(form.value.tax_rate);
  form.value.total_amount = form.value.subtotal + form.value.tax_amount;
}

function openCreateModal() {
  isEditing.value = false;
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + 30);

  form.value = {
    id_invoices: null,
    invoice_number: generateInvoiceNumber(),
    customer_id: '',
    invoice_date: new Date().toISOString().split('T')[0],
    due_date: dueDate.toISOString().split('T')[0],
    items: [{ product_id: null, description: '', quantity: 1, unit_price: 0, amount: 0 }],
    subtotal: 0,
    tax_rate: 0.16,
    tax_amount: 0,
    total_amount: 0,
    pdf_url: '',
    notes: '',
    status: 'pending'
  };
  showModal.value = true;
}

function openEditModal(invoice) {
  isEditing.value = true;
  form.value = {
    id_invoices: invoice.id_invoices,
    invoice_number: invoice.invoice_number,
    customer_id: invoice.customer_id || '',
    invoice_date: invoice.invoice_date,
    due_date: invoice.due_date,
    items: typeof invoice.items === 'string' ? JSON.parse(invoice.items) : invoice.items,
    subtotal: invoice.subtotal,
    tax_rate: invoice.tax_rate,
    tax_amount: invoice.tax_amount,
    total_amount: invoice.total_amount,
    pdf_url: invoice.pdf_url || '',
    notes: invoice.notes || '',
    status: invoice.status
  };
  showModal.value = true;
}

// Función global para sanitizar texto (evitar caracteres problemáticos)
function sanitizeText(text) {
  if (!text) return '';
  return String(text)
    .normalize('NFD') // Descomponer caracteres acentuados
    .replace(/[\u0300-\u036f]/g, '') // Eliminar marcas diacríticas
    .replace(/[^\x00-\x7F]/g, '') // Eliminar caracteres no-ASCII restantes
    .trim();
}

// Función para sanitizar objetos de items
function sanitizeItems(items) {
  return items.map(item => ({
    ...item,
    description: sanitizeText(item.description)
  }));
}

async function saveInvoice() {
  if (!form.value.customer_id || form.value.items.length === 0) {
    alertModalConfig.value = {
      type: 'warning',
      title: 'Campos requeridos',
      message: 'Por favor completa el cliente y al menos un ítem',
      autoClose: 0
    };
    showAlertModal.value = true;
    return;
  }

  loading.value = true;
  try {
    // Sanitizar items antes de guardar
    const sanitizedItems = sanitizeItems(form.value.items);

    const invoiceData = {
      invoice_number: form.value.invoice_number,
      customer_id: form.value.customer_id,
      invoice_date: form.value.invoice_date,
      due_date: form.value.due_date || null,
      items: sanitizedItems,
      subtotal: form.value.subtotal,
      tax_rate: form.value.tax_rate,
      tax_amount: form.value.tax_amount,
      total_amount: form.value.total_amount,
      notes: sanitizeText(form.value.notes) || null,
      status: form.value.status,
      created_by: user?.id_users
    };

    let error, insertedId;
    if (isEditing.value) {
      const result = await supabase
        .from('invoices')
        .update(invoiceData)
        .eq('id_invoices', form.value.id_invoices);
      error = result.error;
      insertedId = form.value.id_invoices;
    } else {
      const result = await supabase
        .from('invoices')
        .insert([invoiceData])
        .select();
      error = result.error;
      insertedId = result.data?.[0]?.id_invoices;
    }

    if (error) throw error;

    // Generar y subir PDF a MinIO + Enviar por correo
    try {
      // Obtener el invoice completo con customer_name y customer_email
      const { data: fullInvoice, error: fetchError } = await supabase
        .from('invoices')
        .select(`
          *,
          customer:customers(name, email)
        `)
        .eq('id_invoices', insertedId)
        .single();

      if (!fetchError && fullInvoice) {
        const invoiceWithCustomer = {
          ...fullInvoice,
          customer_name: sanitizeText(fullInvoice.customer?.name) || 'Cliente',
          customer_email: fullInvoice.customer?.email,
          // Sanitizar items también
          items: typeof fullInvoice.items === 'string'
            ? sanitizeItems(JSON.parse(fullInvoice.items))
            : sanitizeItems(fullInvoice.items)
        };

        // Generar PDF
        const pdfBlob = generatePDFBlob(invoiceWithCustomer);

        // Subir a MinIO
        const pdfUrl = await uploadPDFToMinIO(pdfBlob, invoiceWithCustomer.invoice_number);

        // Actualizar pdf_url en la base de datos
        await supabase
          .from('invoices')
          .update({ pdf_url: pdfUrl })
          .eq('id_invoices', insertedId);

        // Enviar factura por correo si el cliente tiene email
        console.log('📧 Datos del cliente:', {
          email: invoiceWithCustomer.customer_email,
          name: invoiceWithCustomer.customer_name,
          invoice_number: invoiceWithCustomer.invoice_number,
          pdf_url: pdfUrl
        });

        if (invoiceWithCustomer.customer_email && invoiceWithCustomer.customer_email.includes('@')) {
          try {
            const emailResult = await sendInvoiceEmail(
              invoiceWithCustomer.customer_email,
              invoiceWithCustomer.customer_name,
              invoiceWithCustomer.invoice_number,
              invoiceWithCustomer.invoice_date,
              invoiceWithCustomer.total_amount,
              pdfUrl
            );
            console.log('✅ Factura enviada por correo exitosamente:', emailResult);

            // Mostrar notificación al usuario
            alertModalConfig.value = {
              type: 'success',
              title: 'Factura enviada',
              message: `Factura enviada por correo a ${invoiceWithCustomer.customer_email}`,
              autoClose: 3000
            };
            showAlertModal.value = true;
          } catch (emailError) {
            console.error('❌ Error enviando correo:', emailError);
            // Mostrar advertencia pero continuar
            alertModalConfig.value = {
              type: 'warning',
              title: 'Advertencia',
              message: 'La factura se guardó pero no se pudo enviar el correo',
              autoClose: 3000
            };
            showAlertModal.value = true;
          }
        } else {
          console.log('⚠️ Cliente sin email válido, no se envió correo. Email:', invoiceWithCustomer.customer_email);
          alertModalConfig.value = {
            type: 'warning',
            title: 'Sin email',
            message: 'La factura se guardó pero el cliente no tiene email registrado',
            autoClose: 3000
          };
          showAlertModal.value = true;
        }
      }
    } catch (pdfError) {
      console.error('Error generando/subiendo PDF:', pdfError);
      // No mostrar error al usuario, el PDF se puede generar después
    }

    alertModalConfig.value = {
      type: 'success',
      title: isEditing.value ? 'Actualizada' : 'Creada',
      message: `Factura ${isEditing.value ? 'actualizada' : 'creada'} exitosamente`,
      autoClose: 2000
    };
    showAlertModal.value = true;

    showModal.value = false;
    await loadInvoices();
  } catch (error) {
    console.error('Error guardando factura:', error);
    alertModalConfig.value = {
      type: 'error',
      title: 'Error',
      message: 'No se pudo guardar la factura',
      autoClose: 0
    };
    showAlertModal.value = true;
  } finally {
    loading.value = false;
  }
}

async function deleteInvoice(id) {
  confirmModalConfig.value = {
    type: 'danger',
    title: '¿Eliminar factura?',
    message: 'Esta acción no se puede deshacer',
    onConfirm: async () => {
      loading.value = true;
      try {
        // Obtener la factura para acceder al pdf_url
        const { data: invoice, error: fetchError } = await supabase
          .from('invoices')
          .select('pdf_url')
          .eq('id_invoices', id)
          .single();

        if (fetchError) throw fetchError;

        // Eliminar PDF de MinIO si existe
        if (invoice?.pdf_url) {
          try {
            // Extraer el nombre del archivo de la URL
            const urlParts = invoice.pdf_url.split('/');
            const fileName = urlParts[urlParts.length - 1];
            const folderName = urlParts[urlParts.length - 2];
            const key = `${folderName}/${fileName}`;
            await deleteFileFromMinIO(key);
          } catch (minioError) {
            console.error('Error eliminando PDF de MinIO:', minioError);
            // Continuar con la eliminación de la factura aunque falle el PDF
          }
        }

        // Eliminar la factura
        const { error } = await supabase
          .from('invoices')
          .delete()
          .eq('id_invoices', id);

        if (error) throw error;

        alertModalConfig.value = {
          type: 'success',
          title: 'Eliminada',
          message: 'Factura eliminada exitosamente',
          autoClose: 2000
        };
        showAlertModal.value = true;

        await loadInvoices();
      } catch (error) {
        console.error('Error eliminando factura:', error);
        alertModalConfig.value = {
          type: 'error',
          title: 'Error',
          message: 'No se pudo eliminar la factura',
          autoClose: 0
        };
        showAlertModal.value = true;
      } finally {
        loading.value = false;
      }
    }
  };
  showConfirmModal.value = true;
}

// Función para generar el PDF como Blob
function generatePDFBlob(invoice) {
  const doc = new jsPDF();

  // Header
  doc.setFontSize(22);
  doc.setTextColor(124, 92, 255);
  doc.text('FACTURA', 20, 20);

  // Invoice Number
  doc.setFontSize(12);
  doc.setTextColor(0, 0, 0);
  doc.text(`Nº: ${invoice.invoice_number}`, 20, 30);

  // Company Info
  doc.setFontSize(10);
  doc.text('Sushito OPS', 140, 20);
  doc.text('Sistema de Gestión', 140, 26);

  // Dates
  doc.text(`Fecha Emisión: ${new Date(invoice.invoice_date).toLocaleDateString('es-ES')}`, 20, 40);
  doc.text(`Fecha Vencimiento: ${invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('es-ES') : 'No especificada'}`, 20, 46);

  // Customer Info
  doc.setFontSize(11);
  doc.text('CLIENTE:', 20, 60);
  doc.setFontSize(10);
  const customerName = sanitizeText(invoice.customer_name) || 'Cliente';
  doc.text(customerName, 20, 66);

  // Customer Email (si existe)
  if (invoice.customer_email) {
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(`Email: ${invoice.customer_email}`, 20, 72);
    doc.setTextColor(0, 0, 0);
  }

  // Items Table
  const items = typeof invoice.items === 'string' ? JSON.parse(invoice.items) : invoice.items;

  const tableData = items.map(item => [
    sanitizeText(item.description) || 'Producto',
    item.quantity,
    `$${formatNumber(item.unit_price)}`,
    `$${formatNumber(item.amount)}`
  ]);

  const startY = invoice.customer_email ? 82 : 76;

  doc.autoTable({
    startY: startY,
    head: [['Descripcion', 'Cantidad', 'Precio Unit.', 'Total']],
    body: tableData,
    theme: 'grid',
    headStyles: {
      fillColor: [124, 92, 255],
      textColor: [255, 255, 255],
      fontSize: 10,
      fontStyle: 'bold'
    },
    styles: {
      fontSize: 9,
      cellPadding: 5
    }
  });

  // Totals
  const finalY = doc.lastAutoTable.finalY + 10;
  doc.text(`Subtotal:`, 130, finalY);
  doc.text(`$${formatNumber(invoice.subtotal)}`, 170, finalY, { align: 'right' });

  doc.text(`IVA (${(invoice.tax_rate * 100).toFixed(0)}%):`, 130, finalY + 6);
  doc.text(`$${formatNumber(invoice.tax_amount)}`, 170, finalY + 6, { align: 'right' });

  doc.setFontSize(12);
  doc.setFont(undefined, 'bold');
  doc.text(`TOTAL:`, 130, finalY + 15);
  doc.text(`$${formatNumber(invoice.total_amount)}`, 170, finalY + 15, { align: 'right' });

  // Notes
  if (invoice.notes) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    doc.text('Notas:', 20, finalY + 30);
    doc.text(invoice.notes, 20, finalY + 36, { maxWidth: 170 });
  }

  // Footer
  doc.setFontSize(8);
  doc.setTextColor(128, 128, 128);
  doc.text('Gracias por su preferencia', 105, 280, { align: 'center' });

  return doc.output('blob');
}

// Función para subir el PDF a MinIO
async function uploadPDFToMinIO(pdfBlob, invoiceNumber) {
  try {
    // Crear un File object desde el Blob
    const fileName = `Factura-${invoiceNumber}-${Date.now()}.pdf`;
    const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

    // Subir a MinIO
    const url = await uploadFileToMinIO(file, 'facturas/');
    return url;
  } catch (error) {
    console.error('Error subiendo PDF a MinIO:', error);
    throw error;
  }
}

// Función para enviar factura por correo (usando servicio especializado)
async function sendInvoiceEmail(customerEmail, customerName, invoiceNumber, invoiceDate, totalAmount, pdfUrl) {
  try {
    // Formatear fecha
    const formattedDate = new Date(invoiceDate).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });

    // Payload para el servicio especializado de facturas
    // IMPORTANTE: Enviar totalAmount como número puro (sin formatear)
    // El servicio backend se encarga de formatearlo con separadores de miles
    const emailPayload = {
      to: customerEmail,
      customerName: customerName,
      invoiceNumber: invoiceNumber,
      invoiceDate: formattedDate,
      totalAmount: parseFloat(totalAmount), // Enviar como número puro
      pdfUrl: pdfUrl
    };

    console.log('📤 Enviando factura por email:', {
      destinatario: customerEmail,
      factura: invoiceNumber,
      total: parseFloat(totalAmount),
      pdfUrl: pdfUrl
    });

    // URL del servicio especializado de facturas con PDF adjunto
    // Usa el mismo dominio con proxy por defecto para evitar problemas de CORS
    const serviceUrl = import.meta.env.VITE_INVOICE_EMAIL_URL || '/invoice-email/send-invoice';
    console.log('🔗 URL del servicio de facturas:', serviceUrl);

    const response = await fetch(serviceUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(emailPayload),
    });

    console.log('📨 Respuesta del servidor:', response.status, response.statusText);

    if (!response.ok) {
      let errorData;
      try {
        const text = await response.text();
        console.log('📄 Respuesta del servidor (raw):', text.substring(0, 500)); // Solo primeros 500 chars

        // Si es HTML (error de proxy), extraer info útil
        if (text.trim().startsWith('<')) {
          const isHTML = text.includes('<html') || text.includes('<body');
          if (isHTML) {
            console.error('🚫 El servidor devolvió HTML en lugar de JSON. Esto indica que el proxy o servicio no está configurado correctamente.');
            errorData = {
              error: `Proxy/Servicio no configurado (Error ${response.status})`,
              detail: 'El servicio de email de facturas no está disponible o el proxy no está configurado.'
            };
          } else {
            errorData = { error: `Error ${response.status}: ${response.statusText}` };
          }
        } else {
          errorData = JSON.parse(text);
        }
      } catch (parseError) {
        console.error('❌ Error parseando respuesta:', parseError);
        errorData = { error: `Error ${response.status}: ${response.statusText}` };
      }
      console.error('❌ Error del servidor:', errorData);
      throw new Error(errorData.error || errorData.message || `Error ${response.status}`);
    }

    const result = await response.json();
    console.log('✅ Factura enviada exitosamente:', result);
    return result;
  } catch (error) {
    console.error('💥 Error enviando correo de factura:', {
      message: error.message,
      stack: error.stack,
      email: customerEmail,
      invoice: invoiceNumber
    });
    throw error;
  }
}

function downloadPDF(invoice) {
  // Si existe PDF_URL, abrir en nueva pestaña
  if (invoice.pdf_url) {
    window.open(invoice.pdf_url, '_blank');
    return;
  }

  // Si no, generar y descargar
  const pdfBlob = generatePDFBlob(invoice);
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `Factura-${invoice.invoice_number}.pdf`;
  link.click();
  URL.revokeObjectURL(url);
}

function getStatusColor(status) {
  const statusObj = statuses.find(s => s.value === status);
  return statusObj?.color || '#6b7280';
}

function getStatusLabel(status) {
  const statusObj = statuses.find(s => s.value === status);
  return statusObj?.label || status;
}

const totalAmount = computed(() => {
  return invoices.value.reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
});

const paidAmount = computed(() => {
  return invoices.value
    .filter(inv => inv.status === 'paid')
    .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
});

const pendingAmount = computed(() => {
  return invoices.value
    .filter(inv => inv.status === 'pending')
    .reduce((sum, inv) => sum + parseFloat(inv.total_amount || 0), 0);
});

// Función para formatear números con separadores de miles (coma para miles, punto para decimales)
function formatNumber(value) {
  if (!value && value !== 0) return '0.00';
  // Formato estadounidense: 1,234,567.89
  return parseFloat(value).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}
</script>

<template>
  <div class="page-container">
    <div class="header">
      <div class="header-content">
        <h1 class="title">
          <span class="material-symbols-rounded icon">description</span>
          Facturación
        </h1>
        <p class="subtitle">Gestión de facturas y documentos fiscales</p>
      </div>
      <button @click="openCreateModal" class="btn-primary">
        <span class="material-symbols-rounded">add</span>
        Nueva Factura
      </button>
    </div>

    <div class="stats-grid">
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon">receipt_long</span>
        <div class="stat-content">
          <p class="stat-value">{{ invoices.length }}</p>
          <p class="stat-label">Total Facturas</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon paid">check_circle</span>
        <div class="stat-content">
          <p class="stat-value">${{ formatNumber(paidAmount) }}</p>
          <p class="stat-label">Pagadas</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon pending">schedule</span>
        <div class="stat-content">
          <p class="stat-value">${{ formatNumber(pendingAmount) }}</p>
          <p class="stat-label">Pendientes</p>
        </div>
      </div>
      <div class="stat-card">
        <span class="material-symbols-rounded stat-icon">payments</span>
        <div class="stat-content">
          <p class="stat-value">${{ formatNumber(totalAmount) }}</p>
          <p class="stat-label">Total Facturado</p>
        </div>
      </div>
    </div>

    <div class="search-bar">
      <span class="material-symbols-rounded">search</span>
      <input v-model="searchQuery" type="text" placeholder="Buscar por número, cliente o estado..." />
    </div>

    <div v-if="loading && invoices.length === 0" class="loading">
      <span class="material-symbols-rounded spin">progress_activity</span>
      <p>Cargando facturas...</p>
    </div>

    <div v-else-if="filteredInvoices.length === 0" class="empty-state">
      <span class="material-symbols-rounded">description</span>
      <p>{{ searchQuery ? 'No se encontraron facturas' : 'No hay facturas registradas' }}</p>
      <button @click="openCreateModal" class="btn-secondary">Crear primera factura</button>
    </div>

    <div v-else class="table-container">
      <table class="modern-table">
        <thead>
          <tr>
            <th>Nº Factura</th>
            <th>Cliente</th>
            <th>Fecha Emisión</th>
            <th>Fecha Venc.</th>
            <th>Total</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="invoice in filteredInvoices" :key="invoice.id_invoices">
            <td class="invoice-number">{{ invoice.invoice_number }}</td>
            <td>{{ invoice.customer_name }}</td>
            <td>{{ new Date(invoice.invoice_date).toLocaleDateString('es-ES') }}</td>
            <td>{{ invoice.due_date ? new Date(invoice.due_date).toLocaleDateString('es-ES') : '-' }}</td>
            <td class="total-amount">${{ formatNumber(invoice.total_amount) }}</td>
            <td>
              <span class="status-badge" :style="{ backgroundColor: getStatusColor(invoice.status) }">
                {{ getStatusLabel(invoice.status) }}
              </span>
            </td>
            <td>
              <div class="action-buttons">
                <button @click="downloadPDF(invoice)" class="btn-icon" title="Descargar PDF">
                  <span class="material-symbols-rounded">download</span>
                </button>
                <button @click="openEditModal(invoice)" class="btn-icon" title="Editar">
                  <span class="material-symbols-rounded">edit</span>
                </button>
                <button @click="deleteInvoice(invoice.id_invoices)" class="btn-icon danger" title="Eliminar">
                  <span class="material-symbols-rounded">delete</span>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Modal -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-overlay" @click="showModal = false">
        <div class="modal-content large" @click.stop>
          <div class="modal-header">
            <h2>{{ isEditing ? 'Editar Factura' : 'Nueva Factura' }}</h2>
            <button @click="showModal = false" class="btn-close">
              <span class="material-symbols-rounded">close</span>
            </button>
          </div>

          <form @submit.prevent="saveInvoice" class="modal-form">
            <div class="form-section">
              <h3 class="section-title">Información General</h3>
              <div class="form-row">
                <div class="form-group">
                  <label>Nº Factura *</label>
                  <input v-model="form.invoice_number" type="text" readonly class="readonly" />
                </div>
                <div class="form-group">
                  <label>Estado</label>
                  <select v-model="form.status">
                    <option v-for="status in statuses" :key="status.value" :value="status.value">
                      {{ status.label }}
                    </option>
                  </select>
                </div>
              </div>

              <div class="form-row">
                <div class="form-group">
                  <label>Fecha Emisión *</label>
                  <input v-model="form.invoice_date" type="date" required />
                </div>
                <div class="form-group">
                  <label>Fecha Vencimiento *</label>
                  <input v-model="form.due_date" type="date" required />
                </div>
              </div>
            </div>

            <div class="form-section">
              <h3 class="section-title">Cliente</h3>
              <div class="form-row">
                <div class="form-group">
                  <label>Cliente *</label>
                  <select v-model="form.customer_id" required>
                    <option value="">Seleccionar cliente...</option>
                    <option v-for="customer in customers" :key="customer.id_customers" :value="customer.id_customers">
                      {{ customer.name }}
                    </option>
                  </select>
                </div>
              </div>
            </div>

            <div class="form-section">
              <div class="section-header">
                <h3 class="section-title">Ítems de Factura</h3>
                <button type="button" @click="addItem" class="btn-secondary small">
                  <span class="material-symbols-rounded">add</span>
                  Agregar Ítem
                </button>
              </div>

              <div class="items-list">
                <div v-for="(item, index) in form.items" :key="index" class="item-row">
                  <div class="item-fields">
                    <div class="form-group flex-2">
                      <label>Producto/Servicio</label>
                      <select v-model="item.product_id" @change="onProductSelect(item)">
                        <option :value="null">Producto personalizado...</option>
                        <option v-for="product in products" :key="product.id_products" :value="product.id_products">
                          {{ product.name }} - ${{ formatNumber(product.unit_price) }}
                        </option>
                      </select>
                    </div>
                    <div class="form-group flex-2">
                      <label>Descripción</label>
                      <input v-model="item.description" type="text" placeholder="Descripción del ítem" />
                    </div>
                    <div class="form-group flex-1">
                      <label>Cantidad</label>
                      <input v-model="item.quantity" type="number" step="0.01" min="0"
                        @input="calculateItemAmount(item)" />
                    </div>
                    <div class="form-group flex-1">
                      <label>Precio Unit.</label>
                      <input v-model="item.unit_price" type="number" step="0.01" min="0"
                        @input="calculateItemAmount(item)" />
                    </div>
                    <div class="form-group flex-1">
                      <label>Total</label>
                      <input v-model="item.amount" type="text" readonly class="readonly item-total" />
                    </div>
                  </div>
                  <button type="button" @click="removeItem(index)" class="btn-icon danger">
                    <span class="material-symbols-rounded">delete</span>
                  </button>
                </div>
              </div>
            </div>

            <div class="form-section">
              <h3 class="section-title">Totales</h3>
              <div class="totals-section">
                <div class="total-row">
                  <span>Subtotal:</span>
                  <span class="amount">${{ formatNumber(form.subtotal) }}</span>
                </div>
                <div class="total-row">
                  <div class="tax-input">
                    <span>IVA:</span>
                    <input :value="(form.tax_rate * 100).toFixed(0)"
                      @input="e => { form.tax_rate = parseFloat(e.target.value) / 100; calculateTotals(); }"
                      type="number" step="1" min="0" max="100" class="tax-rate" />
                    <span>%</span>
                  </div>
                  <span class="amount">${{ formatNumber(form.tax_amount) }}</span>
                </div>
                <div class="total-row grand-total">
                  <span>TOTAL:</span>
                  <span class="amount">${{ formatNumber(form.total_amount) }}</span>
                </div>
              </div>
            </div>

            <div class="form-section">
              <div class="form-group">
                <label>Notas</label>
                <textarea v-model="form.notes" placeholder="Notas adicionales para la factura..." rows="3"></textarea>
              </div>
            </div>

            <div class="modal-actions">
              <button type="button" @click="showModal = false" class="btn-secondary">
                Cancelar
              </button>
              <button type="submit" class="btn-primary" :disabled="loading">
                <span v-if="loading" class="material-symbols-rounded spin">progress_activity</span>
                <span v-else>{{ isEditing ? 'Actualizar' : 'Guardar' }}</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </Teleport>

    <!-- Modales de Alerta y Confirmación -->
    <AlertModal :show="showAlertModal" :type="alertModalConfig.type" :title="alertModalConfig.title"
      :message="alertModalConfig.message" :auto-close="alertModalConfig.autoClose" @close="showAlertModal = false" />

    <ConfirmModal :show="showConfirmModal" :type="confirmModalConfig.type" :title="confirmModalConfig.title"
      :message="confirmModalConfig.message" @confirm="confirmModalConfig.onConfirm(); showConfirmModal = false"
      @cancel="showConfirmModal = false" />
    <ChatAssistant />
  </div>
</template>

<style scoped>
.page-container {
  padding: 2rem;
  max-width: 1600px;
  margin: 0 auto;
  background: linear-gradient(135deg, #0a0b14 0%, #1a1610 100%);
  min-height: 100vh;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.header-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.title {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 2rem;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}

.title .icon {
  font-size: 2.5rem;
  color: #7c5cff;
}

.subtitle {
  color: var(--muted);
  margin: 0;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.75rem;
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  border: none;
  border-radius: 20px;
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.35);
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
  transition: all 0.5s ease;
}

.stat-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 32px rgba(198, 146, 61, 0.45);
}

.stat-card:hover::before {
  top: -60%;
  right: -60%;
}

.stat-card:nth-child(1) {
  background: linear-gradient(135deg, #f59e0b, #f97316);
}

.stat-card:nth-child(2) {
  background: linear-gradient(135deg, #10b981, #06b6d4);
}

.stat-card:nth-child(3) {
  background: linear-gradient(135deg, #f59e0b, #eab308);
}

.stat-card:nth-child(4) {
  background: linear-gradient(135deg, #7c5cff, #a855f7);
}

.stat-icon {
  font-size: 3rem;
  color: white;
  background: rgba(255, 255, 255, 0.2);
  padding: 1rem;
  border-radius: 16px;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 1;
}

.stat-icon.paid {
  color: white;
  background: rgba(255, 255, 255, 0.2);
}

.stat-icon.pending {
  color: white;
  background: rgba(255, 255, 255, 0.2);
}

.stat-content {
  flex: 1;
  z-index: 1;
}

.stat-value {
  font-size: 2rem;
  font-weight: 800;
  color: white;
  margin: 0 0 0.25rem 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.stat-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  margin: 0;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  margin-bottom: 1.5rem;
}

.search-bar span {
  color: var(--muted);
}

.search-bar input {
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text);
  font-size: 1rem;
  outline: none;
}

.table-container {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.modern-table {
  width: 100%;
  border-collapse: collapse;
}

.modern-table thead {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.1), rgba(223, 192, 94, 0.1));
  border-bottom: 2px solid var(--border);
}

.modern-table th {
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: var(--text);
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.modern-table td {
  padding: 1rem;
  border-bottom: 1px solid var(--border);
  color: var(--text);
}

.modern-table tbody tr:hover {
  background: rgba(198, 146, 61, 0.05);
}

.invoice-number {
  font-weight: 600;
  font-family: 'Courier New', monospace;
}

.total-amount {
  font-weight: 700;
  color: #7c5cff;
}

.status-badge {
  display: inline-block;
  padding: 0.35rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  color: white;
  text-transform: capitalize;
}

.action-buttons {
  display: flex;
  gap: 0.5rem;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-primary {
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  color: white;
  box-shadow: 0 4px 12px rgba(198, 146, 61, 0.3);
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(198, 146, 61, 0.4);
}

.btn-secondary {
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
}

.btn-secondary:hover {
  background: rgba(198, 146, 61, 0.1);
}

.btn-secondary.small {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: rgba(198, 146, 61, 0.1);
  color: #7c5cff;
  cursor: pointer;
  transition: all 0.3s;
}

.btn-icon:hover {
  background: rgba(198, 146, 61, 0.2);
  transform: scale(1.1);
}

.btn-icon.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.btn-icon.danger:hover {
  background: rgba(239, 68, 68, 0.2);
}

.loading,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  text-align: center;
  color: var(--muted);
}

.loading span,
.empty-state span {
  font-size: 4rem;
  margin-bottom: 1rem;
  color: #7c5cff;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 99999;
  padding: 1rem;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modal-content {
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 24px;
  max-width: 700px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 80px rgba(198, 146, 61, 0.25), 0 0 1px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(198, 146, 61, 0.1);
  animation: slideUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px) scale(0.95);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Dark mode para el modal */
:root[data-theme="dark"] .modal-content {
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  border: 2px solid rgba(198, 146, 61, 0.3);
}

.modal-content.large {
  max-width: 950px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 2rem 2rem 1.5rem 2rem;
  border-bottom: 3px solid transparent;
  border-image: linear-gradient(90deg, #7c5cff, #DFC05E, #7c5cff);
  border-image-slice: 1;
  position: sticky;
  top: 0;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.08), rgba(223, 192, 94, 0.08));
  backdrop-filter: blur(10px);
  z-index: 10;
}

:root[data-theme="dark"] .modal-header {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.15), rgba(223, 192, 94, 0.15));
}

.modal-header h2 {
  margin: 0;
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-size: 1.75rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.modal-header h2::before {
  content: '📄';
  font-size: 2rem;
}

.btn-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--muted);
  cursor: pointer;
  transition: all 0.3s;
}

.btn-close:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.modal-form {
  padding: 2rem;
  background: transparent;
}

.form-section {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: rgba(198, 146, 61, 0.03);
  border-radius: 16px;
  border: 1px solid rgba(198, 146, 61, 0.1);
  position: relative;
  overflow: hidden;
}

:root[data-theme="dark"] .form-section {
  background: rgba(198, 146, 61, 0.08);
  border-color: rgba(198, 146, 61, 0.2);
}

.form-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(180deg, #7c5cff, #DFC05E);
  border-radius: 4px 0 0 4px;
}

.section-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: #7c5cff;
  margin: 0 0 1.25rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  letter-spacing: -0.01em;
}

.section-title::before {
  content: '▸';
  font-size: 1.4rem;
  color: #DFC05E;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.form-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.flex-1 {
  flex: 1;
}

.form-group.flex-2 {
  flex: 2;
}

.form-group label {
  font-weight: 700;
  color: #475569;
  font-size: 0.9rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.25rem;
}

:root[data-theme="dark"] .form-group label {
  color: #94a3b8;
}

.form-group input,
.form-group select,
.form-group textarea {
  padding: 0.875rem 1.25rem;
  border: 2px solid rgba(198, 146, 61, 0.15);
  border-radius: 12px;
  background: white;
  color: #1e293b;
  font-size: 1rem;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

:root[data-theme="dark"] .form-group input,
:root[data-theme="dark"] .form-group select,
:root[data-theme="dark"] .form-group textarea {
  background: rgba(255, 255, 255, 0.08);
  color: #f1f5f9;
  border-color: rgba(198, 146, 61, 0.25);
}

.form-group input:hover,
.form-group select:hover,
.form-group textarea:hover {
  border-color: rgba(198, 146, 61, 0.3);
  box-shadow: 0 4px 8px rgba(198, 146, 61, 0.1);
}

.form-group input:focus,
.form-group select:focus,
.form-group textarea:focus {
  outline: none;
  border-color: #7c5cff;
  box-shadow: 0 0 0 4px rgba(198, 146, 61, 0.15), 0 4px 12px rgba(198, 146, 61, 0.2);
  transform: translateY(-1px);
}

.form-group input.readonly {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.12), rgba(223, 192, 94, 0.12));
  font-weight: 700;
  color: #7c5cff;
  cursor: not-allowed;
  border-color: rgba(198, 146, 61, 0.3);
  font-family: 'Courier New', monospace;
  letter-spacing: 0.02em;
}

:root[data-theme="dark"] .form-group input.readonly {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.2), rgba(223, 192, 94, 0.2));
  color: #a78bfa;
}

.items-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.item-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  padding: 1.25rem;
  background: white;
  border-radius: 12px;
  border: 2px solid rgba(198, 146, 61, 0.1);
  box-shadow: 0 2px 8px rgba(198, 146, 61, 0.08);
  transition: all 0.3s ease;
}

:root[data-theme="dark"] .item-row {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(198, 146, 61, 0.2);
}

.item-row:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(198, 146, 61, 0.15);
  border-color: rgba(198, 146, 61, 0.3);
}

.item-fields {
  display: grid;
  grid-template-columns: 1.5fr 2fr 0.8fr 1fr 1fr;
  gap: 0.75rem;
  align-items: flex-end;
  width: 100%;
}

.item-fields .form-group {
  min-width: 0;
  max-width: 100%;
}

.item-total {
  text-align: right;
  padding-right: 1rem !important;
}

.item-row .btn-icon {
  align-self: center;
  flex-shrink: 0;
}

@media (max-width: 1200px) {
  .item-fields {
    grid-template-columns: 1fr 1.5fr;
    gap: 0.75rem;
  }

  .item-row {
    grid-template-columns: 1fr;
  }

  .item-row .btn-icon {
    justify-self: end;
  }
}

.totals-section {
  max-width: 450px;
  margin-left: auto;
  padding: 1.75rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.08), rgba(223, 192, 94, 0.08));
  border-radius: 16px;
  border: 2px solid rgba(198, 146, 61, 0.2);
  box-shadow: 0 8px 24px rgba(198, 146, 61, 0.15);
}

:root[data-theme="dark"] .totals-section {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.15), rgba(223, 192, 94, 0.15));
}

.total-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  color: var(--text);
}

.total-row.grand-total {
  font-size: 1.5rem;
  font-weight: 800;
  padding: 1.25rem;
  margin: 1rem -1rem -1rem -1rem;
  background: linear-gradient(135deg, #7c5cff, #DFC05E);
  color: white !important;
  border-radius: 0 0 14px 14px;
  box-shadow: 0 -4px 20px rgba(198, 146, 61, 0.3);
}

.total-row.grand-total span {
  color: white !important;
}

.total-row.grand-total .amount {
  font-size: 1.75rem;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.total-row .amount {
  font-weight: 600;
}

.tax-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.tax-rate {
  width: 80px;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--background);
  color: var(--text);
  text-align: center;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
  padding: 1.5rem 2rem;
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.05), rgba(223, 192, 94, 0.05));
  border-radius: 0 0 22px 22px;
  margin-left: -2rem;
  margin-right: -2rem;
  margin-bottom: -1.5rem;
}

:root[data-theme="dark"] .modal-actions {
  background: linear-gradient(135deg, rgba(198, 146, 61, 0.1), rgba(223, 192, 94, 0.1));
}

.modal-actions button {
  min-width: 140px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  font-size: 0.9rem;
}

@media (max-width: 768px) {
  .page-container {
    padding: 1rem;
  }

  .header {
    flex-direction: column;
    align-items: stretch;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .form-row {
    grid-template-columns: 1fr;
  }

  .item-row {
    flex-direction: column;
  }

  .item-fields {
    flex-direction: column;
  }

  .table-container {
    overflow-x: auto;
  }

  .modern-table {
    min-width: 900px;
  }
}
</style>
